import React, { useState, createContext, useContext } from 'react';
export type BusinessType = 'product' | 'car' | 'rental' | 'realestate' | 'recruitment' | 'service' | 'generic';
// The catalogue tab uses one consistent label across every business type.
export const CATALOG_LABEL: Record<BusinessType, string> = {
  product: 'Catalogue',
  car: 'Catalogue',
  rental: 'Catalogue',
  realestate: 'Catalogue',
  recruitment: 'Catalogue',
  service: 'Catalogue',
  generic: 'Catalogue'
};
export interface BusinessHub {
  active: boolean;
  type: BusinessType;
  name?: string;
  isNew: boolean;
  logo?: string;
  shopSlug?: string;
}
export interface Package {
  id: string;
  name: string;
  price: number;
  listingLimit: number;
  features: string[];
  popular?: boolean;
}
export const PACKAGES: Package[] = [{
  id: 'Free',
  name: 'Free',
  price: 0,
  listingLimit: 1,
  features: ['1 listing', 'Basic AI conversations', 'Standard support']
}, {
  id: 'Starter',
  name: 'Starter',
  price: 299,
  listingLimit: 3,
  features: ['Up to 3 listings', 'AI customer conversations', 'Basic lead management', 'Email support']
}, {
  id: 'Growth',
  name: 'Growth',
  price: 599,
  listingLimit: 10,
  features: ['Up to 10 listings', 'Full lead intelligence', 'Market intelligence', 'Priority support'],
  popular: true
}, {
  id: 'Pro',
  name: 'Pro',
  price: 1199,
  listingLimit: 40,
  features: ['Up to 40 listings', 'Team roles & permissions', 'Competitor insights', 'Dedicated success manager']
}, {
  id: 'Scale',
  name: 'Scale',
  price: 5000,
  listingLimit: Infinity,
  features: ['100+ listings', 'Unlimited team members', 'API access', 'White-glove onboarding']
}];
interface BusinessContextValue {
  hub: BusinessHub | null;
  activateHub: (type: BusinessType, name?: string) => void;
  markHubSeen: () => void;
  updateHubProfile: (updates: Partial<BusinessHub>) => void;
  // Trial / go-live state. The hub can be built (create/edit/test/draft)
  // before the trial is active; publishing & leads require an active trial.
  trialActive: boolean;
  trialDaysRemaining: number;
  trialPlan: string | null;
  activateTrial: (plan?: string) => void;
  startTrial: (plan?: string) => void;
  // Paid subscription state (after upgrading from trial via card payment).
  subscribed: boolean;
  subscribePlan: (plan: string) => void;
  subscribePackage: (id: string) => void;
  cancelSubscription: () => void;
  currentPlanId: string;
  listingCount: number;
  listingLimit: number;
  canAddListing: boolean;
  publishListings: (n: number) => void;
  unpublishListings: (n: number) => void;
  PACKAGES: Package[];
}
const BusinessContext = createContext<BusinessContextValue | null>(null);
export function useBusiness() {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
}
export function BusinessProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [hub, setHub] = useState<BusinessHub | null>(null);
  const [trialActive, setTrialActive] = useState(false);
  const [trialPlan, setTrialPlan] = useState<string | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string>('Free');
  const [listingCount, setListingCount] = useState(1); // Mock 1 listing used
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const listingLimit = currentPlan.listingLimit;
  const canAddListing = listingCount < listingLimit;
  // Mocked countdown — a freshly activated 7-day trial shows 6 days remaining.
  const trialDaysRemaining = 6;
  const activateHub = (type: BusinessType, name?: string) => {
    // For the rental demo, seed the Easylease branding (logo + handle) so the
    // freshly created hub and its storefront come in fully branded.
    if (type === 'rental') {
      setHub({
        active: true,
        type,
        name: name ?? 'Easylease',
        isNew: true,
        shopSlug: 'easylease',
        logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&q=80&w=200&h=200'
      });
      return;
    }
    const slug = (name ?? '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    setHub({
      active: true,
      type,
      name,
      isNew: true,
      shopSlug: slug || undefined
    });
  };
  const markHubSeen = () => {
    setHub(prev => prev ? {
      ...prev,
      isNew: false
    } : prev);
  };
  const updateHubProfile = (updates: Partial<BusinessHub>) => {
    setHub(prev => prev ? {
      ...prev,
      ...updates
    } : prev);
  };
  const [subscribed, setSubscribed] = useState(false);
  const activateTrial = (plan?: string) => {
    setTrialActive(true);
    if (plan) setTrialPlan(plan);
  };
  const subscribePackage = (id: string) => {
    setCurrentPlanId(id);
    setSubscribed(true);
    setTrialActive(true);
    setTrialPlan(id);
  };
  const subscribePlan = (plan: string) => {
    subscribePackage(plan);
  };
  const cancelSubscription = () => {
    setSubscribed(false);
    setTrialActive(false);
    setTrialPlan(null);
    setCurrentPlanId('Free');
  };
  const publishListings = (n: number) => {
    setListingCount(c => c + n);
  };
  const unpublishListings = (n: number) => {
    setListingCount(c => Math.max(0, c - n));
  };
  return <BusinessContext.Provider value={{
    hub,
    activateHub,
    markHubSeen,
    updateHubProfile,
    trialActive,
    trialDaysRemaining,
    trialPlan,
    activateTrial,
    startTrial: activateTrial,
    subscribed,
    subscribePlan,
    subscribePackage,
    cancelSubscription,
    currentPlanId,
    listingCount,
    listingLimit,
    canAddListing,
    publishListings,
    unpublishListings,
    PACKAGES
  }} data-id="element-564" data-mp="uuho6f70xnc2">
      {children}
    </BusinessContext.Provider>;
}
// Maps a business type to its workspace top-nav tabs.
// The 2nd tab (inventory) is renamed per business type; recruitment adds Candidates.
export function getHubTabs(type: BusinessType): string[] {
  const base = ['Dashboard', CATALOG_LABEL[type], ...(type === 'recruitment' ? ['Candidates'] : []), 'Leads', 'AI Agents', 'Settings'];
  return base;
}
export const BUSINESS_TYPE_LABEL: Record<BusinessType, string> = {
  product: 'Product Seller',
  car: 'Car Dealer',
  rental: 'Car Rental Company',
  realestate: 'Real Estate Agency',
  recruitment: 'Recruitment Agency',
  service: 'Service Provider',
  generic: 'Business'
};